import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError, resolveImage } from "@/lib/api";
import { refreshSiteSettings } from "@/lib/settings";
import { toast } from "sonner";
import { Loader2, Save, Upload, X, Plus, Trash2, AlertTriangle, ShieldAlert } from "lucide-react";

const PLATFORMS = ["facebook", "instagram", "tiktok", "xhs", "wechat", "whatsapp", "other"];

export default function AdminSettings() {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    bank_name: "",
    bank_account_name: "",
    bank_account_number: "",
    whatsapp_number: "",
    site_email: "",
    logo_path: null,
    company_name: "",
    contact_phone: "",
    contact_address_zh: "",
    contact_address_en: "",
    business_hours_zh: "",
    business_hours_en: "",
    social_links: [],
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setForm({
      bank_name: r.data.bank_name || "",
      bank_account_name: r.data.bank_account_name || "",
      bank_account_number: r.data.bank_account_number || "",
      whatsapp_number: r.data.whatsapp_number || "",
      site_email: r.data.site_email || "",
      logo_path: r.data.logo_path || null,
      company_name: r.data.company_name || "",
      contact_phone: r.data.contact_phone || "",
      contact_address_zh: r.data.contact_address_zh || "",
      contact_address_en: r.data.contact_address_en || "",
      business_hours_zh: r.data.business_hours_zh || "",
      business_hours_en: r.data.business_hours_en || "",
      social_links: r.data.social_links || [],
    }));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.patch("/admin/settings", { ...form, social_links: (form.social_links || []).filter((s) => s.url) });
      await refreshSiteSettings();
      toast.success(t("admin.settingsSaved"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const onLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Persist immediately so the new logo shows without needing a second save
      const nextForm = { ...form, logo_path: data.path };
      setForm(nextForm);
      await api.patch("/admin/settings", nextForm);
      await refreshSiteSettings();
      toast.success(t("common.saved"));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const clearLogo = async () => {
    const nextForm = { ...form, logo_path: null };
    setForm(nextForm);
    try {
      await api.patch("/admin/settings", nextForm);
      await refreshSiteSettings();
      toast.success(t("common.saved"));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const inputCls = "w-full px-4 py-2.5 rounded-md border border-[#e5ebeb] focus:border-[#00868e] outline-none bg-white transition-colors duration-200";

  return (
    <div className="p-6 md:p-10 max-w-3xl" data-testid="admin-settings-page">
      <h1 className="font-serif-cn text-3xl text-[#003645] font-semibold">{t("admin.settings")}</h1>

      <form onSubmit={submit} className="mt-8 rounded-lg border border-[#e5ebeb] bg-white p-6 space-y-6">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold">brand</div>
          <div className="mt-4 flex flex-col sm:flex-row items-start gap-6" data-testid="logo-upload-section">
            <div className="h-24 w-40 rounded-md border border-[#e5ebeb] bg-white flex items-center justify-center overflow-hidden">
              {form.logo_path ? (
                <img src={resolveImage(form.logo_path)} alt="Logo preview" className="max-h-20 w-auto object-contain" data-testid="logo-preview" />
              ) : (
                <div className="text-center px-2">
                  <div className="logo-cn text-lg text-[#003645] font-black tracking-tight">晶灵文创</div>
                  <div className="mt-0.5 text-[8px] tracking-[0.28em] text-[#00868e] font-semibold">JINLIN CREATIVE</div>
                </div>
              )}
            </div>
            <div className="flex-1 flex flex-col gap-2">
              <label className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#005870] text-white text-sm font-medium hover:bg-[#003645] cursor-pointer transition-colors duration-200 w-fit" data-testid="logo-upload-btn">
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {form.logo_path ? "Replace logo" : "Upload logo"}
                <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={onLogoUpload} disabled={uploading} data-testid="logo-upload-input" />
              </label>
              {form.logo_path && (
                <button type="button" onClick={clearLogo} className="inline-flex items-center gap-1.5 text-xs text-[#4a6e77] hover:text-red-600 w-fit" data-testid="logo-clear-btn">
                  <X size={12} /> Remove logo (fall back to text)
                </button>
              )}
              <div className="text-xs text-[#4a6e77] leading-relaxed max-w-md">
                Recommended: transparent PNG or SVG, black artwork on transparent background,
                at least 400×160px. Uploaded once, the logo appears in the header and footer
                automatically.
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold">bank</div>
          <div className="mt-3 grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-[#4a6e77] mb-1.5">{t("checkout.bankName")}</label>
              <input className={inputCls} value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} data-testid="settings-bank-name" />
            </div>
            <div>
              <label className="block text-xs text-[#4a6e77] mb-1.5">{t("checkout.accountName")}</label>
              <input className={inputCls} value={form.bank_account_name} onChange={(e) => setForm({ ...form, bank_account_name: e.target.value })} data-testid="settings-bank-account-name" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs text-[#4a6e77] mb-1.5">{t("checkout.accountNumber")}</label>
              <input className={inputCls} value={form.bank_account_number} onChange={(e) => setForm({ ...form, bank_account_number: e.target.value })} data-testid="settings-bank-account-number" />
            </div>
          </div>
        </div>

        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#00868e] font-semibold">contact</div>
          <div className="mt-3 grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-[#4a6e77] mb-1.5">WhatsApp</label>
              <input className={inputCls} placeholder="+60123456789" value={form.whatsapp_number} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} data-testid="settings-wa" />
            </div>
            <div>
              <label className="block text-xs text-[#4a6e77] mb-1.5">Email</label>
              <input type="email" className={inputCls} value={form.site_email} onChange={(e) => setForm({ ...form, site_email: e.target.value })} data-testid="settings-email" />
            </div>
          </div>
        </div>

        {/* Company + Contact */}
        <div className="pt-6 border-t border-[#e5ebeb]">
          <div className="text-xs uppercase tracking-widest text-[#00868e] font-semibold">公司资讯 / Company & Contact</div>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-[#4a6e77] mb-1.5">公司名称 / Company name</label>
              <input className={inputCls} placeholder="CiAO Ideology" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="settings-company-name" />
            </div>
            <div>
              <label className="block text-xs text-[#4a6e77] mb-1.5">联络电话 / Phone</label>
              <input className={inputCls} value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="settings-contact-phone" />
            </div>
            <div>
              <label className="block text-xs text-[#4a6e77] mb-1.5">地址 (中)</label>
              <input className={inputCls} value={form.contact_address_zh} onChange={(e) => setForm({ ...form, contact_address_zh: e.target.value })} data-testid="settings-address-zh" />
            </div>
            <div>
              <label className="block text-xs text-[#4a6e77] mb-1.5">Address (EN)</label>
              <input className={inputCls} value={form.contact_address_en} onChange={(e) => setForm({ ...form, contact_address_en: e.target.value })} data-testid="settings-address-en" />
            </div>
            <div>
              <label className="block text-xs text-[#4a6e77] mb-1.5">营业时间 (中)</label>
              <input className={inputCls} value={form.business_hours_zh} onChange={(e) => setForm({ ...form, business_hours_zh: e.target.value })} data-testid="settings-hours-zh" />
            </div>
            <div>
              <label className="block text-xs text-[#4a6e77] mb-1.5">Business hours (EN)</label>
              <input className={inputCls} value={form.business_hours_en} onChange={(e) => setForm({ ...form, business_hours_en: e.target.value })} data-testid="settings-hours-en" />
            </div>
          </div>
        </div>

        {/* Social links */}
        <div className="pt-6 border-t border-[#e5ebeb]" data-testid="settings-social-block">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-widest text-[#00868e] font-semibold">社群链接 / Social Links</div>
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, social_links: [...(f.social_links || []), { platform: "instagram", url: "", label: "" }] }))}
              className="inline-flex items-center gap-1 text-xs text-[#005870] hover:text-[#00868e]"
              data-testid="add-site-social"
            >
              <Plus size={12} /> Add
            </button>
          </div>
          <div className="mt-3 space-y-2">
            {(form.social_links || []).map((s, i) => (
              <div key={i} className="flex flex-col sm:flex-row gap-2" data-testid={`site-social-row-${i}`}>
                <select value={s.platform} onChange={(e) => setForm((f) => ({ ...f, social_links: f.social_links.map((x, j) => i === j ? { ...x, platform: e.target.value } : x) }))} className={inputCls + " sm:w-40"}>
                  {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
                <input placeholder="https://..." value={s.url} onChange={(e) => setForm((f) => ({ ...f, social_links: f.social_links.map((x, j) => i === j ? { ...x, url: e.target.value } : x) }))} className={inputCls + " flex-1"} data-testid={`site-social-url-${i}`} />
                <input placeholder="Label" value={s.label || ""} onChange={(e) => setForm((f) => ({ ...f, social_links: f.social_links.map((x, j) => i === j ? { ...x, label: e.target.value } : x) }))} className={inputCls + " sm:w-40"} />
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, social_links: f.social_links.filter((_, j) => j !== i) }))}
                  className="h-10 w-10 flex items-center justify-center rounded-md border border-[#e5ebeb] text-red-600 hover:border-red-200"
                  data-testid={`remove-site-social-${i}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            {(form.social_links || []).length === 0 && (
              <div className="text-xs text-[#4a6e77]">尚未添加社群链接 / No social links yet</div>
            )}
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#005870] text-white font-medium hover:bg-[#003645] transition-colors duration-200 disabled:opacity-60"
          data-testid="save-settings-btn"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {t("admin.saveSettings")}
        </button>
      </form>

      <DangerZone />
    </div>
  );
}

// ============ Danger Zone: Full Data Wipe ============
function DangerZone() {
  const { i18n } = useTranslation();
  const zh = !i18n.language?.startsWith("en");
  const [open, setOpen] = useState(false);
  const REQUIRED_PHRASE = zh ? "清空数据" : "DELETE";

  const [phrase, setPhrase] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const phraseOk = phrase.trim() === REQUIRED_PHRASE;
  const canSubmit = phraseOk && password.length > 0 && !submitting;

  const reset = () => {
    setPhrase("");
    setPassword("");
    setError("");
    setResult(null);
  };

  const close = () => {
    if (submitting) return;
    setOpen(false);
    reset();
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/admin/wipe-data", { password, phrase: phrase.trim() });
      setResult(data);
      toast.success(zh ? "数据已清空" : "Data wiped");
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      // Extra client-side hint for common cases
      if (err?.response?.status === 403) {
        setError(zh ? "密码不正确 — 未执行任何删除操作" : "Password incorrect — nothing was deleted");
      } else if (err?.response?.status === 429) {
        setError(zh ? "已有清空操作在执行中，请稍候" : "A wipe is already in progress, please wait");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const wipeItems = zh
    ? ["会员/代理用户（除您以外）", "产品、订单、支付/佣金/积分记录", "预约、时段、命理师傅", "课程、报名", "文章、Angelina 内容", "报告请求、提现记录", "上传文件记录", "所有其他登录会话"]
    : ["All member/agent users (except you)", "Products, orders, payment/commission/points transactions", "Bookings, slots, masters", "Events, signups", "Articles, Angelina content", "Report requests, withdrawals", "Uploaded file records", "All other login sessions"];

  const keepItems = zh
    ? ["您的管理员账号", "站点设置（银行、社群、联系方式）", "页面标题", "产品分类（结构）", "文章分类", "本次审计记录"]
    : ["Your admin account", "Site settings (bank, social, contact)", "Page titles", "Product categories (structure)", "Article categories", "This audit record"];

  return (
    <>
      <section
        className="mt-16 rounded-lg border-2 border-red-200 bg-red-50/40 p-6 md:p-8"
        data-testid="danger-zone"
      >
        <div className="flex items-start gap-3">
          <div className="mt-0.5 shrink-0 h-10 w-10 rounded-full bg-red-100 text-red-700 flex items-center justify-center">
            <ShieldAlert size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-serif-cn text-xl text-red-900 font-semibold">
              {zh ? "危险操作 · Danger Zone" : "Danger Zone"}
            </h2>
            <p className="mt-1 text-sm text-red-800/80">
              {zh
                ? "以下操作将永久删除数据且不可恢复。请务必谨慎，建议先在预览环境验证。"
                : "Actions here permanently delete data and cannot be undone. Verify on preview first."}
            </p>
            <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 rounded-md bg-white border border-red-200 p-4">
              <div className="min-w-0">
                <div className="font-medium text-red-900">
                  {zh ? "清空所有数据" : "Wipe All Data"}
                </div>
                <div className="text-xs text-red-700/80 mt-1">
                  {zh ? "删除所有交易数据与用户，仅保留站点结构与您的管理员账号。" : "Deletes all transactional data and users, keeping only site structure and your admin account."}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-red-600 text-white font-medium hover:bg-red-700 transition-colors duration-200 shrink-0"
                data-testid="wipe-open-btn"
              >
                <AlertTriangle size={14} />
                {zh ? "清空所有数据" : "Wipe All Data"}
              </button>
            </div>
          </div>
        </div>
      </section>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 overflow-y-auto"
          data-testid="wipe-modal"
          role="dialog"
          aria-modal="true"
        >
          <div className="my-8 w-full max-w-2xl rounded-lg bg-white shadow-2xl border-2 border-red-300">
            <div className="flex items-start justify-between p-5 border-b border-red-100 bg-red-50/60 rounded-t-lg">
              <div className="flex items-center gap-3">
                <ShieldAlert size={20} className="text-red-700 shrink-0" />
                <h3 className="font-serif-cn text-lg text-red-900 font-semibold">
                  {zh ? "确认清空所有数据" : "Confirm Wipe All Data"}
                </h3>
              </div>
              <button
                onClick={close}
                disabled={submitting}
                className="text-red-700 hover:text-red-900 disabled:opacity-40"
                aria-label="close"
                data-testid="wipe-close-btn"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {!result && (
                <>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="rounded-md border border-red-200 bg-red-50/40 p-4">
                      <div className="text-xs uppercase tracking-widest text-red-700 font-semibold mb-2">
                        {zh ? "将被删除" : "Will be deleted"}
                      </div>
                      <ul className="text-sm text-red-900 space-y-1.5 list-disc pl-4">
                        {wipeItems.map((it, i) => <li key={i}>{it}</li>)}
                      </ul>
                    </div>
                    <div className="rounded-md border border-[#00868e]/30 bg-[#00868e]/5 p-4">
                      <div className="text-xs uppercase tracking-widest text-[#005870] font-semibold mb-2">
                        {zh ? "将保留" : "Will be kept"}
                      </div>
                      <ul className="text-sm text-[#003645] space-y-1.5 list-disc pl-4">
                        {keepItems.map((it, i) => <li key={i}>{it}</li>)}
                      </ul>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-[#4a6e77] mb-1.5">
                      {zh ? `请输入确认短语 "${REQUIRED_PHRASE}"` : `Type "${REQUIRED_PHRASE}" to confirm`}
                    </label>
                    <input
                      value={phrase}
                      onChange={(e) => setPhrase(e.target.value)}
                      placeholder={REQUIRED_PHRASE}
                      className={`w-full px-4 py-2.5 rounded-md border outline-none transition-colors duration-200 ${
                        phrase && !phraseOk ? "border-red-400 bg-red-50/40" : "border-[#e5ebeb] focus:border-red-400"
                      }`}
                      data-testid="wipe-phrase-input"
                      autoComplete="off"
                      disabled={submitting}
                    />
                    {phrase && !phraseOk && (
                      <div className="mt-1 text-xs text-red-700">
                        {zh ? "短语不匹配" : "Phrase does not match"}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs text-[#4a6e77] mb-1.5">
                      {zh ? "请重新输入管理员密码" : "Re-enter admin password"}
                    </label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-md border border-[#e5ebeb] focus:border-red-400 outline-none transition-colors duration-200"
                      data-testid="wipe-password-input"
                      autoComplete="new-password"
                      disabled={submitting}
                    />
                  </div>

                  {error && (
                    <div className="rounded-md border border-red-300 bg-red-50 text-red-800 text-sm px-4 py-3" data-testid="wipe-error">
                      {error}
                    </div>
                  )}

                  <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={close}
                      disabled={submitting}
                      className="px-5 py-2.5 rounded-full border border-[#e5ebeb] text-[#003645] hover:bg-[#e5ebeb]/40 transition-colors duration-200 disabled:opacity-60"
                      data-testid="wipe-cancel-btn"
                    >
                      {zh ? "取消" : "Cancel"}
                    </button>
                    <button
                      type="button"
                      onClick={submit}
                      disabled={!canSubmit}
                      className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full bg-red-600 text-white font-medium hover:bg-red-700 transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="wipe-confirm-btn"
                    >
                      {submitting ? <Loader2 size={14} className="animate-spin" /> : <AlertTriangle size={14} />}
                      {zh ? "永久删除" : "Delete Forever"}
                    </button>
                  </div>
                </>
              )}

              {result && (
                <div className="space-y-4" data-testid="wipe-result">
                  <div className="rounded-md border border-[#00868e]/40 bg-[#00868e]/5 text-[#003645] px-4 py-3">
                    {zh ? "✓ 数据已清空。" : "✓ Data wiped successfully."}
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-widest text-[#4a6e77] font-semibold mb-2">
                      {zh ? "删除计数" : "Deleted counts"}
                    </div>
                    <div className="rounded-md border border-[#e5ebeb] overflow-hidden">
                      <table className="w-full text-sm">
                        <tbody>
                          {Object.entries(result.deleted || {}).map(([k, v]) => (
                            <tr key={k} className="border-b border-[#e5ebeb] last:border-0">
                              <td className="px-3 py-1.5 text-[#4a6e77] font-mono">{k}</td>
                              <td className="px-3 py-1.5 text-right text-[#003645] font-medium">{v}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="text-xs text-[#4a6e77]">
                    {zh ? "审计 ID: " : "Audit ID: "}<span className="font-mono">{result.audit_id}</span>
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => { close(); window.location.reload(); }}
                      className="px-5 py-2.5 rounded-full bg-[#005870] text-white font-medium hover:bg-[#003645] transition-colors duration-200"
                      data-testid="wipe-done-btn"
                    >
                      {zh ? "完成" : "Done"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
